import { useTranslations } from "@jugl-web/utils";
import { FilterSectionLayout } from "@web-src/modules/filters/components/FilterSectionLayout";
import { FilterPill } from "@web-src/modules/filters/components/FilterPill";
import { Button } from "@jugl-web/ui-components";
import {
  InteractiveContainer,
  Popover,
} from "@jugl-web/ui-components/cross-platform";
import { DatePicker } from "@jugl-web/ui-components/cross-platform/DatePicker";
import format from "date-fns/format";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { useFilters } from "../../hooks/useFilters";

interface ComponentProps {
  title: string;
  filters: ReturnType<typeof useFilters>;
}

const handleDateSelect = (
  from: Date | null,
  to: Date | null,
  title: string,
  filterState: Record<string, string[]>,
  setFilter: (key: string, value: string[]) => void
) => {
  const dates = [];

  if (from) dates.push(format(from, "yyyy-MM-dd"));
  if (to) dates.push(format(to, "yyyy-MM-dd"));
  const currentDates = filterState[title] || [];
  const selectionString = dates.join(",");
  setFilter(title, [...currentDates, selectionString]);
};

export const TypeDateFilter = ({ title, filters }: ComponentProps) => {
  const { t } = useTranslations();
  const { filterState, setFilter, clearFilter } = filters;

  const selectedDates = filterState[title] || [];

  return (
    <FilterSectionLayout>
      <FilterSectionLayout.Header
        title={title}
        endSlot={
          selectedDates.length > 0 ? null : (
            <Popover
              className="w-[340px] rounded-xl"
              placement="bottom-start"
              renderTrigger={({ Trigger, triggerRef }) => (
                <Trigger ref={triggerRef} as={InteractiveContainer}>
                  <Button
                    color="grey"
                    className="h-8 px-3 text-sm"
                    iconEnd={<PlusIcon />}
                  >
                    {t({ id: "common.add", defaultMessage: "Add" })}
                  </Button>
                </Trigger>
              )}
            >
              {({ onClose }) => (
                <DatePicker
                  onSubmit={(from, to) => {
                    handleDateSelect(
                      from,
                      to ?? null,
                      title,
                      filterState,
                      setFilter
                    );
                    onClose();
                  }}
                  onClose={onClose}
                  className="p-4"
                  showCancelButton
                  mode="range"
                />
              )}
            </Popover>
          )
        }
      />
      {selectedDates.length > 0 && (
        <FilterSectionLayout.PillsContainer>
          {selectedDates.map((selectionString, index) => {
            const dates = selectionString.split(",");
            const formatDate = (date: string) =>
              format(new Date(`${date}T00:00:00`), "MMMM d, yyyy");
            return (
              <FilterPill
                key={selectionString}
                label={
                  dates.length === 2
                    ? `${formatDate(dates[0])} - ${formatDate(dates[1])}`
                    : formatDate(dates[0])
                }
                startIcon={<CalendarIcon />}
                onRemove={() => {
                  const newSelections = selectedDates.filter(
                    (_, i) => i !== index
                  );
                  if (newSelections.length === 0) clearFilter(title);
                  else setFilter(title, newSelections);
                }}
              />
            );
          })}
        </FilterSectionLayout.PillsContainer>
      )}
    </FilterSectionLayout>
  );
};
